/** A positioned account tag, in uploaded-file coordinate space (x,y are 0..1). */
export interface UserTag {
  username: string;
  x: number;
  y: number;
}

/** Instagram allows up to 3 collaborators per media. */
export const MAX_COLLABORATORS = 3;

/** Normalises a raw username input: drop leading @, trim, remove spaces, lowercase. */
export function sanitizeUsername(raw: string): string {
  return raw.trim().replace(/^@+/, '').replace(/\s+/g, '').toLowerCase();
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

/**
 * Converts a click position expressed as a fraction of the displayed (object-cover)
 * preview box into a fraction of the uploaded media file.
 */
export function previewPointToMediaFraction(
  fx: number,
  fy: number,
  naturalAspect: number,
  boxAspect: number,
): { x: number; y: number } {
  // Invalid aspects (e.g. an image whose dimensions are not loaded yet) — fall back to
  // a safe passthrough rather than silently collapsing all coordinates to the centre.
  if (!(naturalAspect > 0) || !(boxAspect > 0)) {
    return { x: clamp01(fx), y: clamp01(fy) };
  }
  let ix = fx;
  let iy = fy;
  if (naturalAspect > boxAspect) {
    ix = 0.5 + (fx - 0.5) * (boxAspect / naturalAspect);
  } else if (naturalAspect < boxAspect) {
    iy = 0.5 + (fy - 0.5) * (naturalAspect / boxAspect);
  }
  return { x: clamp01(ix), y: clamp01(iy) };
}

/** Whether `raw` can be added to the existing collaborator list. */
export function canAddCollaborator(existing: string[], raw: string): boolean {
  const name = sanitizeUsername(raw);
  if (!name) return false;
  if (existing.length >= MAX_COLLABORATORS) return false;
  return !existing.map(sanitizeUsername).includes(name);
}

/** Inverse of previewPointToMediaFraction: file fraction -> displayed-box percentage (0..100), clamped. */
export function mediaFractionToPreviewPercent(
  ix: number,
  iy: number,
  naturalAspect: number,
  boxAspect: number,
): { left: number; top: number } {
  const clampPct = (v: number) => Math.min(100, Math.max(0, v));
  let lx = ix;
  let ty = iy;
  if (naturalAspect > boxAspect) {
    lx = 0.5 + (ix - 0.5) * (naturalAspect / boxAspect);
  } else if (naturalAspect < boxAspect) {
    ty = 0.5 + (iy - 0.5) * (boxAspect / naturalAspect);
  }
  return { left: clampPct(lx * 100), top: clampPct(ty * 100) };
}
